import React, { useEffect, useMemo, useState } from "react";
import { X, AlertTriangle, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";
import { findMismatchedClient } from "./uploadMatching";

/**
 * UploadConfirmationModal (Phase 9.5)
 *
 * Modal di conferma obbligatorio prima di ogni upload. Rende in evidenza il
 * nome del cliente destinatario e l'elenco dei file, e mostra un warning
 * rosso se il nome di un file corrisponde a un cliente DIVERSO da quello
 * selezionato (algoritmo in `uploadMatching.js`).
 *
 * Props:
 * - client: {id, company_name} — cliente selezionato per l'upload
 * - files: File[] — lista file da caricare (single-file resta un array di 1)
 * - clients: [{id, company_name}, ...] — tutti i clienti attivi per matching
 * - onConfirm: () => Promise<void> — chiamata al click "Conferma"
 * - onCancel: () => void — chiamata al click "Annulla"
 * - saving: bool — disabilita bottoni durante upload
 */

function fmtSize(bytes) {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fileIcon(file) {
    const type = (file?.type || "").toLowerCase();
    if (type.startsWith("image/")) return <ImageIcon size={16} strokeWidth={2} />;
    if (type === "application/pdf") return <FileText size={16} strokeWidth={2} />;
    return <FileIcon size={16} strokeWidth={2} />;
}

export default function UploadConfirmationModal({ client, files, clients, onConfirm, onCancel, saving }) {
    // Per ogni file, calcola eventuale mismatch (client diverso che matcha).
    // Memoized: ricalcolato solo se cambia files/client/clients.
    const fileMismatches = useMemo(() => {
        return (files || []).map((f) => ({
            file: f,
            mismatchedClient: findMismatchedClient(f.name, client?.id, clients),
        }));
    }, [files, client, clients]);

    const hasAnyMismatch = fileMismatches.some((fm) => fm.mismatchedClient !== null);

    // ESC per chiudere (equivalente a Annulla se non saving)
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !saving) onCancel(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onCancel, saving]);

    // Guard contro doppio-click / doppio-submit
    const [submitting, setSubmitting] = useState(false);
    const doConfirm = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (submitting || saving) return;
        setSubmitting(true);
        try {
            await onConfirm();
        } finally {
            setSubmitting(false);
        }
    };

    const confirmLabel = hasAnyMismatch ? "Conferma comunque" : "Conferma e carica";
    const isBusy = saving || submitting;

    return (
        <div
            className="lt-modal-backdrop"
            onClick={() => { if (!isBusy) onCancel(); }}
            data-testid="upload-confirm-backdrop"
        >
            <div
                className="lt-modal"
                onClick={(e) => e.stopPropagation()}
                data-testid="upload-confirm-modal"
                style={{ maxWidth: 560, width: "92vw", maxHeight: "88vh", display: "flex", flexDirection: "column" }}
            >
                <div className="lt-modal__head">
                    <h2 className="lt-modal__title" style={{ margin: 0 }}>Conferma caricamento</h2>
                    <button
                        className="lt-icon-btn"
                        onClick={onCancel}
                        aria-label="Chiudi"
                        disabled={isBusy}
                        data-testid="upload-confirm-close"
                    >
                        <X size={16} />
                    </button>
                </div>

                <form
                    method="post"
                    action="#"
                    noValidate
                    onSubmit={doConfirm}
                    style={{ padding: 24, overflowY: "auto", flex: 1, minHeight: 0 }}
                >
                    {/* Nome cliente in evidenza (Archivo heavy, size H2, accent) */}
                    <div style={{ marginBottom: 20 }} data-testid="upload-confirm-client-heading">
                        <div style={{
                            fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                            fontSize: 11, letterSpacing: 1.6, textTransform: "uppercase",
                            color: "#8A8F88", marginBottom: 4,
                        }}>
                            Destinatario
                        </div>
                        <div style={{
                            fontFamily: "Archivo, ui-sans-serif, system-ui",
                            fontSize: 26, fontWeight: 800, color: "#181B20", lineHeight: 1.15,
                        }} data-testid="upload-confirm-client-name">
                            {client?.company_name || "—"}
                        </div>
                    </div>

                    {/* Warning banner rosso per ogni file con mismatch */}
                    {fileMismatches.filter((fm) => fm.mismatchedClient).map((fm, idx) => (
                        <div
                            key={`mismatch-${idx}-${fm.file.name}`}
                            data-testid={`upload-confirm-warning-${idx}`}
                            style={{
                                background: "rgba(178, 58, 72, 0.08)",
                                border: "1px solid rgba(178, 58, 72, 0.45)",
                                borderLeft: "3px solid #B23A48",
                                borderRadius: 3,
                                padding: "12px 14px",
                                marginBottom: 12,
                                display: "flex",
                                gap: 10,
                                alignItems: "flex-start",
                            }}
                        >
                            <AlertTriangle size={18} strokeWidth={2} style={{ color: "#B23A48", flexShrink: 0, marginTop: 1 }} />
                            <div style={{ fontSize: 13, color: "#181B20", lineHeight: 1.5 }}>
                                {files.length > 1 ? (
                                    <>
                                        File <strong style={{ fontFamily: "IBM Plex Mono, monospace" }}>&laquo;{fm.file.name}&raquo;</strong> contiene <strong>&laquo;{fm.mismatchedClient.company_name}&raquo;</strong> ma stai caricando su <strong>&laquo;{client?.company_name}&raquo;</strong>. Verifica prima di confermare.
                                    </>
                                ) : (
                                    <>
                                        Il nome del file contiene <strong>&laquo;{fm.mismatchedClient.company_name}&raquo;</strong> ma stai caricando su <strong>&laquo;{client?.company_name}&raquo;</strong>. Verifica prima di confermare.
                                    </>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Testo di conferma */}
                    <p style={{ fontSize: 14, color: "#5F6560", marginBottom: 16, lineHeight: 1.5 }} data-testid="upload-confirm-text">
                        Stai caricando {files.length === 1 ? "questo documento" : `questi ${files.length} documenti`} per <strong style={{ color: "#181B20" }}>{client?.company_name}</strong>. Confermi che appartengono a questo cliente?
                    </p>

                    {/* Elenco file */}
                    <div data-testid="upload-confirm-file-list" style={{ marginBottom: 8 }}>
                        <div style={{
                            fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                            fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase",
                            color: "#8A8F88", marginBottom: 8,
                        }}>
                            {files.length === 1 ? "File" : `${files.length} file`}
                        </div>
                        <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                            {fileMismatches.map((fm, idx) => (
                                <li
                                    key={`f-${idx}-${fm.file.name}`}
                                    data-testid={`upload-confirm-file-${idx}`}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 10,
                                        padding: "10px 12px",
                                        border: fm.mismatchedClient
                                            ? "1px solid rgba(178,58,72,0.35)"
                                            : "1px solid #E3E5E1",
                                        background: fm.mismatchedClient ? "rgba(178,58,72,0.04)" : "#F7F7F5",
                                        borderRadius: 3,
                                        marginBottom: 6,
                                    }}
                                >
                                    <span style={{ color: fm.mismatchedClient ? "#B23A48" : "#5F6560" }}>{fileIcon(fm.file)}</span>
                                    <span style={{
                                        flex: 1,
                                        fontSize: 13,
                                        color: "#181B20",
                                        fontFamily: "IBM Plex Mono, ui-monospace, monospace",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}>
                                        {fm.file.name}
                                    </span>
                                    <span style={{ fontSize: 12, color: "#8A8F88", flexShrink: 0 }}>
                                        {fmtSize(fm.file.size)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </form>

                <div className="lt-form-actions" style={{ padding: "16px 24px", borderTop: "1px solid #E3E5E1", display: "flex", justifyContent: "flex-end", gap: 12 }}>
                    <button
                        type="button"
                        className="lt-btn-secondary"
                        onClick={onCancel}
                        disabled={isBusy}
                        data-testid="upload-confirm-cancel"
                    >
                        Annulla
                    </button>
                    <button
                        type="button"
                        className="lt-btn-primary"
                        onClick={doConfirm}
                        disabled={isBusy}
                        data-testid="upload-confirm-submit"
                    >
                        {isBusy ? "Caricamento…" : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
